"use client";

import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { StoreApi } from "zustand";
import { toast } from "@/components/ui/sonner";
import {
  mergeAddonsPricing,
  productItemsToAddons,
  productItemsToPricing,
  splitAddonEntries,
} from "@/features/addons/lib/product-items";
import { subItemsToAddons } from "@/features/addons/lib/sub-items";
import { getAddonsErrorMessage } from "@/features/addons/lib/sub-items-error";
import {
  type AddonsConfigStore,
  createAddonsConfigStore,
} from "@/features/addons/store/addons-config-store";
import type { ConfigurationItemAddon } from "@/features/addons/types/configuration-item-addon.type";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { useAddonCartChangeEvents } from "@/features/analytics/hooks/use-addon-cart-change-events";
import { useUpdateCart } from "@/features/cart/api/hooks/use-update-cart";
import { useCartStore } from "@/features/cart/components/cart-provider";
import { productItemsForItem } from "@/features/cart/helpers/product-items";
import type { CartItem, CartProductItem } from "@/features/cart/types";
import { getUnavailableProductId } from "@/features/cart/utils/unavailable-product-error";

const isSameSelection = (
  left: ConfigurationItemAddon[],
  right: ConfigurationItemAddon[],
) =>
  left.length === right.length &&
  left.every((entry) =>
    right.some(
      (other) =>
        other.addonId === entry.addonId &&
        other.date === entry.date &&
        other.quantity === entry.quantity,
    ),
  );

// The whole selection of a position as the cart holds it: the catering's
// addons from its sub-items plus the Foodango products riding with it.
const persistedEntries = (
  item: CartItem,
  productItems: CartProductItem[],
): ConfigurationItemAddon[] => [
  ...subItemsToAddons(item.configurationItem.subItems),
  ...productItemsToAddons(productItems),
];

// Addon store for a single cart position on the cart list (step 1). Seeded
// from the item's persisted sub-items and products and pushed back through
// PUT /cart/{id} on every committed change, mirroring the edit flow of the
// configuration page (MPL-3694). The item is already in the cart here, so
// every change is persisted immediately.
export const useCartItemAddons = (
  item: CartItem,
  {
    categories = [],
    cateringName,
  }: {
    categories?: DietAddonGroupDietAddonsRead[];
    cateringName?: string;
  } = {},
): {
  store: StoreApi<AddonsConfigStore>;
  // Quote of the whole selection — addons and products — for the summary rows.
  pricing: SubItemsPricing | null;
} => {
  const t = useTranslations();
  const cartId = useCartStore((state) => state.cartId);
  const cartProductItems = useCartStore((state) => state.productItems);
  const updateCartStore = useCartStore((state) => state.updateCart);
  const reportAddonCartChanges = useAddonCartChangeEvents();

  const itemId = item.configurationItem.id;
  const productItems = useMemo(
    () => productItemsForItem(cartProductItems, itemId),
    [cartProductItems, itemId],
  );
  const pricing = useMemo(
    () =>
      mergeAddonsPricing(
        item.subItemsPricing,
        productItemsToPricing(productItems),
      ),
    [item.subItemsPricing, productItems],
  );

  const storeRef = useRef<StoreApi<AddonsConfigStore>>(undefined);
  if (!storeRef.current) {
    storeRef.current = createAddonsConfigStore(
      persistedEntries(item, productItems),
    );
  }

  const reportedAddonsRef = useRef(persistedEntries(item, productItems));

  // A rollback writes to the store as well; without this guard the resulting
  // store change would be persisted straight back through PUT.
  const isRollingBackRef = useRef(false);

  // The cart validates sub-items and products before writing anything, so a
  // rejection leaves it exactly as it was (MPL-3694). Restore the selection it
  // still holds instead of leaving the list showing addons that were never
  // saved.
  const rollbackToCart = useCallback(() => {
    isRollingBackRef.current = true;
    storeRef.current
      ?.getState()
      .seedAddons(persistedEntries(item, productItems));
    isRollingBackRef.current = false;
  }, [item, productItems]);

  // A product the backend cannot price any more (left the offer, MPL-3829)
  // is dropped from the selection: on success the route already wrote the cart
  // without it, so the store just follows without another PUT; on a rejected
  // write the store change re-persists the selection minus the product.
  const dropUnavailableProduct = useCallback(
    (productId: string, persist: boolean) => {
      isRollingBackRef.current = !persist;
      storeRef.current?.getState().setAddonEntries(productId, []);
      isRollingBackRef.current = false;
      toast.info(t("cart.addons.errors.productRemoved"));
    },
    [t],
  );

  const { isPending, mutate: updateCart } = useUpdateCart({
    onSuccess: ({ cartId: nextCartId, cart, droppedProductId }) => {
      updateCartStore(nextCartId, cart, "update", t);
      if (droppedProductId) {
        dropUnavailableProduct(droppedProductId, false);
      }
    },
    onError: (error) => {
      const unavailableProductId = getUnavailableProductId(error);
      if (unavailableProductId) {
        rollbackToCart();
        dropUnavailableProduct(unavailableProductId, true);
        return;
      }
      toast.error(getAddonsErrorMessage(error, t));
      rollbackToCart();
    },
  });

  const persistAddons = useCallback(() => {
    if (isRollingBackRef.current) {
      return;
    }

    const addons = storeRef.current?.getState().getAllAddons() ?? [];
    const previous = reportedAddonsRef.current;
    reportedAddonsRef.current = addons;

    // The catering's addons stay on the position, the products of other
    // merchants go to the cart's product collection (MPL-3829).
    const { subItems, productItems: productSelection } = splitAddonEntries({
      entries: addons,
      categories,
      dietMerchantId: item.configurationItem.merchantId,
    });

    updateCart(
      {
        cartId,
        item: { ...item.configurationItem, subItems },
        productItems: productSelection,
      },
      {
        // Reported only once the cart accepted the change: a rejected write is
        // rolled back, so the selection it describes never existed (MPL-3873).
        onSuccess: () =>
          reportAddonCartChanges({
            cateringName,
            categories,
            next: addons,
            previous,
            // The quote the cart holds before this change still prices the
            // addons being removed, at the amounts the summary showed for them.
            pricing,
          }),
      },
    );
  }, [
    cartId,
    cateringName,
    categories,
    item.configurationItem,
    pricing,
    reportAddonCartChanges,
    updateCart,
  ]);

  // The store only changes when the user commits an addon selection (add /
  // edit / remove), never on the initial seed, so every emission is a real
  // change worth persisting.
  useEffect(() => storeRef.current?.subscribe(persistAddons), [persistAddons]);

  useEffect(() => {
    if (isPending) {
      return;
    }

    const persisted = persistedEntries(item, productItems);
    if (isSameSelection(persisted, reportedAddonsRef.current)) {
      return;
    }

    reportedAddonsRef.current = persisted;
    isRollingBackRef.current = true;
    storeRef.current?.getState().seedAddons(persisted);
    isRollingBackRef.current = false;
  }, [isPending, item, productItems]);

  return { store: storeRef.current, pricing };
};
